import React, { FunctionComponent, ReactElement, Fragment, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrop, useDragLayer } from 'react-dnd';
import { components as commonComponents, hooks as commonHooks } from '@sitebuilder/common';
import { actionCreators } from '../reducers/templateEditor';
import { StateShape as ReduxStateShape } from '../reducers';

const NotSupportedComponent: FunctionComponent<
    React.PropsWithChildren<{ type: string }>
> = (props: { type: string }): ReactElement => (
    <div>{`Component ${props.type} is not supported`}</div>
);

export function getDescendantWidgetIds(
    componentTree: Record<string, any>,
    rootId: string
): string[] {
    const queue = [rootId];
    const descendants: string[] = [];

    while (queue.length > 0) {
        const currentId = queue.shift();
        const node = componentTree[currentId!];

        if (!node || !node.contentPlaceHolders) continue;

        for (const placeholder of Object.values(node.contentPlaceHolders) as any) {
            for (const childId of placeholder) {
                descendants.push(childId);
                queue.push(childId);
            }
        }
    }

    return descendants;
}

interface PlaceholderProps {
    data?: Record<string, unknown>;
    contentPlaceHolderId: string;
    parentWidgetInstanceId: string;
    renderNull?: boolean;
}

interface NewWidgetInputProps {
    contentPlaceHolderId: string;
    parentWidgetInstanceId: string;
    index: number;
}

const NewWidgetInput: FunctionComponent<React.PropsWithChildren<NewWidgetInputProps>> = ({
    contentPlaceHolderId,
    parentWidgetInstanceId,
    index,
}) => {
    const dispatch = useDispatch();
    const appContext = commonHooks.useAppContext();

    const handleDrop = (item: {
        id: string;
        widgetUUID: string;
        dragMode: 'move' | 'copy' | 'create';
        isRefComponent: boolean;
        isPublicRefComponent: boolean;
    }) => {
        if (item.dragMode === 'copy') {
            dispatch(
                actionCreators.createDuplicateComponent({
                    parentId: parentWidgetInstanceId || '',
                    widgetUUID: item.widgetUUID,
                    contentPlaceHolderId,
                    index,
                })
            );
        } else if (item.dragMode === 'move') {
            dispatch(
                actionCreators.moveComponent({
                    parentId: parentWidgetInstanceId || '',
                    widgetUUID: item.widgetUUID,
                    contentPlaceHolderId,
                    index,
                })
            );
        } else {
            dispatch(
                actionCreators.createComponent({
                    componentId: item.id,
                    parentId: parentWidgetInstanceId || '',
                    contentPlaceHolderId,
                    index,
                    isRefComponent: item.isRefComponent,
                    isPublicRefComponent: item.isPublicRefComponent,
                    widgetFactory: appContext.widgetFactory,
                })
            );
        }
    };

    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'WIDGET',
        drop: handleDrop,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    return (
        <div
            ref={drop}
            className="add-widget-container"
            style={{ backgroundColor: isOver ? '#ededed' : 'white' }}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            {isOver && <img src="add.svg" className="add-widget-icon" alt="add widget" />}
        </div>
    );
};

export const EditorPlaceholder: FunctionComponent<React.PropsWithChildren<PlaceholderProps>> = ({
    data,
    contentPlaceHolderId,
    parentWidgetInstanceId,
    renderNull,
}: PlaceholderProps): ReactElement => {
    const dispatch = useDispatch();
    const appContext = commonHooks.useAppContext();

    const { widgetInstances, parentWidgetInstance, selectedId } = useSelector(
        (state: ReduxStateShape) => {
            const widgetInstances = state.templateEditor.document?.widgetInstances;
            const parentWidgetInstance = (widgetInstances || {})[parentWidgetInstanceId];
            return {
                widgetInstances,
                parentWidgetInstance,
                selectedId: state.templateEditor.selectedId,
            };
        }
    );

    const {
        item,
        isDragging: isDraggingWidget,
        dragType,
    } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        isDragging: monitor.isDragging(),
        dragType: monitor.getItemType(),
    }));
    const draggingWidgetId = item?.widgetUUID || '';

    const childWidgetInstanceIds = parentWidgetInstance
        ? parentWidgetInstance.contentPlaceHolders[contentPlaceHolderId]
        : [];

    const descendantWidgetIds = draggingWidgetId
        ? getDescendantWidgetIds(widgetInstances || {}, draggingWidgetId)
        : [];

    const showNewWidgetInput =
        dragType?.toString() === 'WIDGET' &&
        !childWidgetInstanceIds?.some((childWidgetInstanceId: string) => {
            if (!widgetInstances) {
                return false;
            }

            const widgetInstance = widgetInstances[childWidgetInstanceId];
            return draggingWidgetId && descendantWidgetIds.includes(widgetInstance?.id);
        });

    return (
        <>
            {isDraggingWidget && showNewWidgetInput && (
                <NewWidgetInput
                    contentPlaceHolderId={contentPlaceHolderId}
                    parentWidgetInstanceId={parentWidgetInstanceId}
                    index={0}
                />
            )}

            {widgetInstances &&
                childWidgetInstanceIds &&
                childWidgetInstanceIds.map((childWidgetInstanceId: string, i: number) => {
                    const widgetInstance = widgetInstances[childWidgetInstanceId];

                    if (!widgetInstance) {
                        return null;
                    }

                    const isSelected = widgetInstance.id === selectedId;
                    const widget = appContext.widgetFactory.getWidget(widgetInstance.widgetId);

                    if (!widget) {
                        return (
                            <div
                                key={i}
                                className={`placeholder ${isSelected ? 'selected' : ''}`}
                                id={`comp-${widgetInstance.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    isSelected
                                        ? dispatch(actionCreators.deselectElement())
                                        : dispatch(actionCreators.selectElement(widgetInstance.id));
                                }}
                            >
                                <NotSupportedComponent type={widgetInstance?.widgetId || ''} />
                            </div>
                        );
                    }

                    if (renderNull) {
                        return '__';
                    }

                    const Comp = widget.templateView;

                    return (
                        <Fragment key={i}>
                            <commonComponents.ErrorBoundary.ErrorBoundary>
                                <Suspense fallback={null}>
                                    <div
                                        id={`placeholder-${widgetInstance.id}`}
                                        className={`placeholder ${isSelected ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            isSelected
                                                ? dispatch(actionCreators.deselectElement())
                                                : dispatch(
                                                      actionCreators.selectElement(
                                                          widgetInstance.id
                                                      )
                                                  );
                                        }}
                                    >
                                        <Comp
                                            widgetInstanceId={childWidgetInstanceId}
                                            data={data}
                                        />
                                    </div>
                                </Suspense>
                            </commonComponents.ErrorBoundary.ErrorBoundary>

                            {isDraggingWidget &&
                                dragType?.toString() === 'WIDGET' &&
                                childWidgetInstanceIds.length - 1 !== i &&
                                !descendantWidgetIds.includes(widgetInstance.id) && (
                                    <NewWidgetInput
                                        key={`new-widget-above-${i}`}
                                        contentPlaceHolderId={contentPlaceHolderId}
                                        parentWidgetInstanceId={parentWidgetInstanceId}
                                        index={i + 1}
                                    />
                                )}
                        </Fragment>
                    );
                })}

            {isDraggingWidget && showNewWidgetInput && (
                <NewWidgetInput
                    contentPlaceHolderId={contentPlaceHolderId}
                    parentWidgetInstanceId={parentWidgetInstanceId}
                    index={-1}
                />
            )}
        </>
    );
};
